import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

// Every item AUTOXIUM stores in the visitor's browser. Keep this list in sync with
// the code: any new cookie/localStorage key, and especially any analytics or
// third-party script, must be added here (non-essential ones also need a consent
// mechanism before they're introduced).
const storageItems = [
  {
    name: 'autoxium_token',
    type: 'Local storage',
    purpose: 'Keeps you signed in after you log in. Removed when you log out.',
    duration: 'Until you log out (the sign-in itself expires after 24 hours)',
  },
  {
    name: 'uds-simulator-input-mode',
    type: 'Local storage',
    purpose: 'Remembers whether you chose Guided or Raw input in the UDS Diagnostic Simulator.',
    duration: 'Until you clear your browser storage',
  },
];

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen" data-testid="cookie-policy-page">
      <PageHero
        eyebrow="Legal"
        title="Cookie Policy"
        description="What AUTOXIUM stores in your browser, and why."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium mb-8">
            <p className="text-sm text-muted-foreground">Last updated: September 26, 2026</p>

            <h2>The Short Version</h2>
            <p>
              AUTOXIUM uses <strong>only strictly necessary storage</strong>. We do not use
              analytics, advertising, tracking, or social-media cookies, and no third-party
              scripts run on the site. Because everything we store is needed for a feature
              you've asked for, we don't show a cookie consent banner.
            </p>

            <h2>What Are Cookies and Local Storage</h2>
            <p>
              Cookies and local storage are small pieces of data a website saves in your
              browser so it can remember information between page views. AUTOXIUM currently
              sets no cookies at all; it uses your browser's local storage for the items
              below.
            </p>

            <h2>What We Store</h2>
          </div>

          {/* Phones: one card per item (a 4-column table is unreadable at 375px). */}
          <div className="max-w-3xl mb-10 space-y-3 sm:hidden">
            {storageItems.map((item) => (
              <dl key={item.name} className="rounded-xl border border-structure bg-white p-4 text-sm space-y-2">
                <dt className="font-mono text-xs font-semibold break-all">{item.name}</dt>
                <dd className="text-muted-foreground"><span className="font-semibold text-foreground">Type: </span>{item.type}</dd>
                <dd className="text-muted-foreground"><span className="font-semibold text-foreground">Purpose: </span>{item.purpose}</dd>
                <dd className="text-muted-foreground"><span className="font-semibold text-foreground">Duration: </span>{item.duration}</dd>
              </dl>
            ))}
          </div>

          <div className="max-w-3xl mb-10 rounded-xl border border-structure overflow-x-auto hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storageItems.map((item) => (
                  <TableRow key={item.name}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{item.name}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{item.type}</TableCell>
                    <TableCell className="text-sm min-w-[200px]">{item.purpose}</TableCell>
                    <TableCell className="text-sm min-w-[160px]">{item.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="max-w-3xl prose-autoxium">
            <h2>Managing Storage</h2>
            <p>
              You can delete these items at any time through your browser's "Cookies and
              site data" or "Privacy" settings. If you remove <code>autoxium_token</code> you
              will be signed out; removing the simulator setting simply resets it to Guided
              mode.
            </p>

            <h2>Changes to This Policy</h2>
            <p>
              If we ever introduce optional cookies (for example, analytics), we will update
              this page first and ask for your consent before setting them. See our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> for how we handle personal
              data, or email <a href="mailto:contact@autoxium.com">contact@autoxium.com</a>{' '}
              with any questions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
